import { useSearchParams } from "@solidjs/router";
import { BigNumber } from "bignumber.js";
import log from "loglevel";
import {
    Show,
    createEffect,
    createMemo,
    on,
    onCleanup,
    onMount,
} from "solid-js";

import AddressInput from "../components/AddressInput";
import Asset from "../components/Asset";
import CopyButton from "../components/CopyButton";
import CreateButton from "../components/CreateButton";
import Fees from "../components/Fees";
import InvoiceInput from "../components/InvoiceInput";
import QrScan from "../components/QrScan";
import Reverse from "../components/Reverse";
import SwapLimits from "../components/SwapLimits";
import SettingsCog from "../components/settings/SettingsCog";
import SettingsMenu from "../components/settings/SettingsMenu";
import { BTC, LN, getAssetNetwork } from "../consts/Assets";
import { Denomination, Side } from "../consts/Enums";
import { useCreateContext } from "../context/Create";
import { useGlobalContext } from "../context/Global";
import Pair, { RequiredInput } from "../utils/Pair";
import {
    calculateDigits,
    convertAmount,
    formatAmount,
    formatDenomination,
    getValidationRegex,
} from "../utils/denomination";
import { isMobile } from "../utils/helper";
import { isDeferredInvoiceDestination } from "../utils/invoice";

// TODO: formatted amounts should be *instant* and not depend on quote being calculated

const Create = () => {
    let receiveAmountRef: HTMLInputElement | undefined;
    let sendAmountRef: HTMLInputElement | undefined;

    const [searchParams, setSearchParams] = useSearchParams();

    const {
        separator,
        setSeparator,
        setDenomination,
        denomination,
        t,
        notify,
        embeddedMode,
    } = useGlobalContext();
    const {
        pair,
        setPair,
        invoice,
        setInvoice,
        invoiceValid,
        setInvoiceValid,
        setInvoiceError,
        addressValid,
        sendAmount,
        setSendAmount,
        receiveAmount,
        setReceiveAmount,
        sendAmountFormatted,
        setSendAmountFormatted,
        receiveAmountFormatted,
        setReceiveAmountFormatted,
        resetAmounts,
        amountChanged,
        setAmountChanged,
        minimum,
        maximum,
        limitsLoading,
        setAmountValid,
        boltzFee,
        minerFee,
        quoteLoading,
        setQuoteLoading,
        quoteError,
        setQuoteError,
    } = useCreateContext();
    let quoteRequestId = 0;

    const receiveAmountQuoteLoading = createMemo(
        () => quoteLoading() && amountChanged() === Side.Send,
    );
    const sendAmountQuoteLoading = createMemo(
        () => quoteLoading() && amountChanged() === Side.Receive,
    );
    const limitActionsLoading = createMemo(
        () => limitsLoading() || quoteLoading(),
    );
    const requiresInvoiceInput = createMemo(
        () =>
            pair().requiredInput === RequiredInput.Invoice ||
            (pair().requiredInput === RequiredInput.Unknown &&
                pair().toAsset === LN),
    );
    // Quotes are computed locally from the pair data, so they resolve at once
    const loadingGuard = (fn: (isCurrent: () => boolean) => Promise<void>) => {
        const runQuote = async (isCurrent: () => boolean) => {
            setQuoteError(undefined);
            try {
                await fn(isCurrent);
            } catch (error) {
                log.error("Amount quote failed", error);
                if (!isCurrent()) {
                    return;
                }
                setQuoteError("error_no_quote");
                if (amountChanged() === Side.Receive) {
                    setSendAmount(BigNumber(0));
                } else {
                    setReceiveAmount(BigNumber(0));
                }
                validateAmount();
            }
        };

        ++quoteRequestId;
        setQuoteLoading(false);
        const id = quoteRequestId;
        void runQuote(() => id === quoteRequestId);
    };

    onCleanup(() => {
        setQuoteLoading(false);
    });

    // if btc and amount > 10, switch to sat
    // user failed to notice the non satoshi denomination
    const changeDenomination = (amount: string) => {
        if (amount === "") return;
        if (denomination() === Denomination.Btc && Number(amount) >= 10) {
            setDenomination(Denomination.Sat);
        } else if (denomination() === Denomination.Sat && Number(amount) < 1) {
            setDenomination(Denomination.Btc);
        }
    };

    const isEmptyAmount = (amount: string): boolean => {
        return amount === "";
    };

    const clearStaleInvoice = () => {
        const currentInvoice = invoice();
        if (
            currentInvoice === "" ||
            isDeferredInvoiceDestination(currentInvoice)
        ) {
            return;
        }

        setInvoice("");
        setInvoiceValid(false);
        setInvoiceError(undefined);
        notify("success", t("invoice_cleared_amount_changed"));
    };

    const changeReceiveAmount = (evt: InputEvent) => {
        const target = evt.currentTarget as HTMLInputElement;
        const amount = target.value
            .trim()
            .replaceAll(" ", "")
            .replaceAll(",", ".");

        setAmountChanged(Side.Receive);
        clearStaleInvoice();

        if (isEmptyAmount(amount)) {
            ++quoteRequestId;
            setQuoteLoading(false);
            resetAmounts();
            validateAmount();
            return;
        }

        changeDenomination(amount);
        const satAmount = convertAmount(
            pair().toAsset,
            BigNumber(amount),
            denomination(),
        );

        setReceiveAmount(satAmount);
        loadingGuard(async (isCurrent) => {
            if (satAmount.isZero()) {
                setAmountChanged(Side.Receive);
                resetAmounts();
                validateAmount();
                return;
            }
            const sendAmount = await pair().calculateSendAmount(
                satAmount,
                minerFee(),
            );
            if (!isCurrent()) {
                return;
            }
            setSendAmount(sendAmount);
            validateAmount();
        });
    };

    const changeSendAmount = (evt: InputEvent) => {
        const target = evt.currentTarget as HTMLInputElement;
        const amount = target.value
            .trim()
            .replaceAll(" ", "")
            .replaceAll(",", ".");

        setAmountChanged(Side.Send);
        clearStaleInvoice();

        if (isEmptyAmount(amount)) {
            ++quoteRequestId;
            setQuoteLoading(false);
            resetAmounts();
            validateAmount();
            return;
        }

        changeDenomination(amount);
        const satAmount = convertAmount(
            pair().fromAsset,
            BigNumber(amount),
            denomination(),
        );

        setSendAmount(satAmount);
        loadingGuard(async (isCurrent) => {
            const newReceiveAmount = await pair().calculateReceiveAmount(
                satAmount,
                minerFee(),
            );
            if (!isCurrent()) {
                return;
            }
            setReceiveAmount(newReceiveAmount);
            validateAmount();
        });
    };

    const validateInput = (evt: KeyboardEvent) => {
        const input = evt.currentTarget as HTMLInputElement;
        const keycode = evt.key;
        if (keycode === "." || keycode === ",") {
            setSeparator(keycode);
            // switch to BTC denomination
            if (denomination() == "sat") {
                setDenomination(Denomination.Btc);
            }
        }
        const hasDot = input.value.includes(".") || input.value.includes(",");
        const regex =
            denomination() == "sat" || hasDot ? /[0-9]/ : /[0-9]|\.|,/;
        if (!regex.test(keycode)) {
            evt.stopPropagation();
            evt.preventDefault();
        }
    };

    const sanitizeInputValue = (value: string) =>
        value.replace(",", ".").replace(" ", "");

    const validatePaste = (evt: ClipboardEvent) => {
        const clipboardData =
            evt.clipboardData ||
            (globalThis as unknown as { clipboardData: DataTransfer })
                .clipboardData;
        const pastedData = clipboardData
            .getData("Text")
            .replace(/\s+/g, "")
            .trim();
        if (!getValidationRegex(maximum()).test(pastedData)) {
            evt.stopPropagation();
            evt.preventDefault();
            notify("error", t("paste_invalid"));
            return;
        }

        if (pastedData.includes(".") || pastedData.includes(",")) {
            setSeparator(pastedData.includes(".") ? "." : ",");
        }

        const input = evt.currentTarget as HTMLInputElement;
        // prevent pasting the same value
        if (
            input.value &&
            sanitizeInputValue(pastedData) === sanitizeInputValue(input.value)
        ) {
            evt.stopPropagation();
            evt.preventDefault();
            return;
        }
        // replace values from input before pasting
        input.value = "";
    };

    const validateAmount = () => {
        const setCustomValidity = (val: string, isZero: boolean) => {
            [sendAmountRef, receiveAmountRef].forEach((ref) => {
                if (ref === undefined) {
                    return;
                }
                ref.setCustomValidity(val);
                if (!isZero && val !== "") {
                    ref.classList.add("invalid");
                } else {
                    ref.classList.remove("invalid");
                }
            });
        };

        setCustomValidity("", false);

        const quoteErrorKey = quoteError();
        if (
            quoteErrorKey !== undefined &&
            (sendAmount().isGreaterThan(0) || receiveAmount().isGreaterThan(0))
        ) {
            setCustomValidity(t(quoteErrorKey), false);
            setAmountValid(false);
            return;
        }

        const amount = Number(sendAmount());
        if (amount > 0 && receiveAmount().isZero()) {
            setCustomValidity(t("error_zero_quote"), false);
            setAmountValid(false);
            return;
        }

        const lessThanMin = amount === 0 || amount < minimum();

        if (lessThanMin || amount > maximum()) {
            const params = {
                amount: formatAmount(
                    BigNumber(lessThanMin ? minimum() : maximum()),
                    denomination(),
                    separator(),
                    pair().fromAsset,
                ),
                denomination: formatDenomination(
                    denomination(),
                    pair().fromAsset,
                ),
            };
            const label = lessThanMin ? "minimum_amount" : "maximum_amount";
            const errorMsg = t(label, params);
            setCustomValidity(errorMsg, amount === 0);
            setAmountValid(false);
            return;
        }
        setAmountValid(true);
    };

    const setAmount = (amount: number) => {
        setAmountChanged(Side.Send);
        clearStaleInvoice();
        loadingGuard(async (isCurrent) => {
            setSendAmount(BigNumber(amount));
            const newReceiveAmount = await pair().calculateReceiveAmount(
                BigNumber(amount),
                minerFee(),
            );
            if (!isCurrent()) {
                return;
            }
            setReceiveAmount(newReceiveAmount);
            validateAmount();
            sendAmountRef?.focus();
        });
    };

    const setMaxAmount = () => {
        setAmount(maximum());
    };

    onMount(() => {
        sendAmountRef?.focus();
    });

    createEffect(
        on([boltzFee, minerFee, pair], () => {
            loadingGuard(async (isCurrent) => {
                if (amountChanged() === Side.Receive) {
                    if (receiveAmount().isZero()) {
                        setSendAmount(BigNumber(0));
                    } else {
                        const result = await pair().calculateSendAmount(
                            receiveAmount(),
                            minerFee(),
                        );
                        if (!isCurrent()) {
                            return;
                        }
                        setSendAmount(result);
                    }
                } else {
                    const result = await pair().calculateReceiveAmount(
                        sendAmount(),
                        minerFee(),
                    );
                    if (!isCurrent()) {
                        return;
                    }
                    setReceiveAmount(result);
                }

                validateAmount();
            });
        }),
    );

    createEffect(() => {
        const rAmount = Number(receiveAmount());
        if (rAmount > 0) {
            setReceiveAmountFormatted(
                formatAmount(
                    BigNumber(rAmount),
                    denomination(),
                    separator(),
                    pair().toAsset,
                ).toString(),
            );
        } else {
            setReceiveAmountFormatted("");
        }
        const sAmount = Number(sendAmount());
        if (sAmount > 0) {
            setSendAmountFormatted(
                formatAmount(
                    BigNumber(sAmount),
                    denomination(),
                    separator(),
                    pair().fromAsset,
                ).toString(),
            );
        } else {
            setSendAmountFormatted("");
        }
    });

    createEffect(
        on(
            [() => searchParams.sendAsset, () => searchParams.receiveAsset],
            ([sendAsset, receiveAsset]) => {
                const nextSendAsset = Array.isArray(sendAsset)
                    ? sendAsset[0]
                    : sendAsset;
                const nextReceiveAsset = Array.isArray(receiveAsset)
                    ? receiveAsset[0]
                    : receiveAsset;

                if (!nextSendAsset && !nextReceiveAsset) return;

                setPair((current) => {
                    const fromAsset = nextSendAsset ?? current.fromAsset;
                    const toAsset = nextReceiveAsset ?? current.toAsset;

                    if (
                        fromAsset === current.fromAsset &&
                        toAsset === current.toAsset
                    ) {
                        return current;
                    }

                    return new Pair(current.pairs, fromAsset, toAsset);
                });
                setSearchParams(
                    { sendAsset: null, receiveAsset: null },
                    { replace: true },
                );
            },
        ),
    );

    // validate amounts when invoice is valid, because we
    // set the amount based on invoice amount if amount is 0
    createEffect(() => {
        if (invoiceValid()) {
            validateAmount();
        }
    });
    createEffect(() => {
        if (addressValid()) {
            validateAmount();
        }
    });

    // Re-validate when min/max change (they are set asynchronously)
    createEffect(
        on([minimum, maximum], () => {
            if (sendAmount().isGreaterThan(0)) {
                validateAmount();
            }
        }),
    );

    return (
        <div class="frame">
            <SettingsCog />
            <h2 class="frame-title" data-testid="create-swap-title">
                {t("create_swap")}
            </h2>
            <div class="icons">
                <div>
                    <Asset side={Side.Send} signal={() => pair().fromAsset} />
                    <div class="amount-field input-with-label">
                        <div class="amount-input-wrap">
                            <span class="amount-row-label">{t("send")}</span>
                            <SwapLimits
                                maximum={maximum()}
                                maxLabel={t("max")}
                                loading={limitActionsLoading()}
                                maximumEnabled={maximum() > 0}
                                onSelectAmount={setAmount}
                                onSelectMaximum={setMaxAmount}
                            />
                            <Show when={sendAmountQuoteLoading()}>
                                <div
                                    class="amount-value-skeleton"
                                    aria-hidden="true"
                                />
                            </Show>
                            <input
                                ref={sendAmountRef}
                                autofocus
                                required
                                type="text"
                                placeholder="0"
                                maxlength={
                                    maximum() > 0
                                        ? calculateDigits(
                                              maximum(),
                                              denomination(),
                                          )
                                        : undefined
                                }
                                inputmode={
                                    denomination() == "btc"
                                        ? "decimal"
                                        : "numeric"
                                }
                                id="sendAmount"
                                data-testid="sendAmount"
                                autocomplete="off"
                                disabled={sendAmountQuoteLoading()}
                                classList={{
                                    "amount-input--quote-pending":
                                        sendAmountQuoteLoading(),
                                }}
                                aria-busy={sendAmountQuoteLoading()}
                                value={sendAmountFormatted()}
                                onPaste={(e) => validatePaste(e)}
                                onKeyPress={(e) => validateInput(e)}
                                onInput={(e) => changeSendAmount(e)}
                            />
                        </div>
                        <label for="sendAmount" class="input-label">
                            {getAssetNetwork(pair().fromAsset)}
                        </label>
                    </div>
                </div>
                <Reverse />
                <div>
                    <Asset side={Side.Receive} signal={() => pair().toAsset} />
                    <div class="amount-field input-with-label">
                        <div class="amount-input-wrap">
                            <span class="amount-row-label">{t("receive")}</span>
                            <Show when={receiveAmountQuoteLoading()}>
                                <div
                                    class="amount-value-skeleton"
                                    aria-hidden="true"
                                />
                            </Show>
                            <input
                                ref={receiveAmountRef}
                                required
                                type="text"
                                placeholder="0"
                                maxlength={
                                    maximum() > 0
                                        ? calculateDigits(
                                              maximum(),
                                              denomination(),
                                          )
                                        : undefined
                                }
                                inputmode={
                                    denomination() == "btc"
                                        ? "decimal"
                                        : "numeric"
                                }
                                id="receiveAmount"
                                data-testid="receiveAmount"
                                autocomplete="off"
                                disabled={receiveAmountQuoteLoading()}
                                classList={{
                                    "amount-input--quote-pending":
                                        receiveAmountQuoteLoading(),
                                }}
                                aria-busy={receiveAmountQuoteLoading()}
                                value={receiveAmountFormatted()}
                                onPaste={(e) => validatePaste(e)}
                                onKeyPress={(e) => validateInput(e)}
                                onInput={(e) => changeReceiveAmount(e)}
                            />
                        </div>
                        <label for="receiveAmount" class="input-label">
                            {getAssetNetwork(pair().toAsset)}
                        </label>
                    </div>
                </div>
            </div>
            <Fees />
            <hr class="spacer" />
            <Show
                when={
                    pair().requiredInput === RequiredInput.Address ||
                    (pair().requiredInput === RequiredInput.Unknown &&
                        pair().toAsset !== LN)
                }>
                <AddressInput />
                <hr class="spacer" />
            </Show>
            <Show when={requiresInvoiceInput()}>
                <Show when={receiveAmount().isGreaterThan(0)}>
                    <CopyButton
                        label="copy_invoice_amount"
                        labelValues={{
                            denomination: formatDenomination(
                                denomination(),
                                BTC,
                            ),
                        }}
                        btnClass="btn btn-light"
                        data={() =>
                            formatAmount(
                                receiveAmount(),
                                denomination(),
                                // A plain decimal point: wallets parse it whatever the locale
                                ".",
                                BTC,
                            )
                        }
                    />
                    <hr class="spacer" />
                </Show>
                <InvoiceInput />
                <hr class="spacer" />
            </Show>
            <Show when={isMobile()}>
                <QrScan />
                <hr class="spacer" />
            </Show>
            <CreateButton />
            <Show when={embeddedMode()}>
                <div class="embedded-branding">
                    <a
                        href="https://lightningfork.com"
                        target="_blank"
                        rel="noopener noreferrer">
                        Powered by Lightning Fork Swap
                    </a>
                </div>
            </Show>
            <SettingsMenu />
        </div>
    );
};

export default Create;
